import { useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { User, Loader2, X, HelpCircle } from 'lucide-react';
import { format } from 'date-fns';
import Header from '../components/layout/Header';
import BannerAdComponent from '../components/adverts/BannerAdComponent';
import AdSlot from '../components/ads/AdSlot';
import { useGetSquareQuery } from '../api/squares/getSquares';
import { useTenant } from '../contexts/TenantContext';
import { buildRulesPlacement, generateAdDivId } from '../config/ads.config';
import { useOverLogoPlacement } from '../hooks/useOverLogoPlacement';
import { formatPrizePool } from '../utils/formatPrizePool';
import { getTeamCode } from '../utils/teamCodes';
import { EMBED_BOTTOM_CHROME } from '../utils/embedChrome';
import {
  APP_COLUMN_BG,
  APP_GUTTER_BG,
  DESKTOP_COLUMN,
  DESKTOP_COLUMN_FRAME,
  READABLE_CONTENT,
} from '../utils/desktopColumn';

export const RULES_POPUP_BOTTOM_INSET = EMBED_BOTTOM_CHROME;

/**
 * Popup shell fits the iframe viewport (100%), not device dvh.
 * Panel bottom aligns above parent tab bar; height fills the remaining embed area.
 */
export const RULES_POPUP_PANEL_STYLE = {
  bottom: RULES_POPUP_BOTTOM_INSET,
  height: `calc(100% - ${EMBED_BOTTOM_CHROME})`,
  maxHeight: `calc(100% - ${EMBED_BOTTOM_CHROME})`,
} as const;

/** Play Now footer — pinned at panel bottom with padding above embed chrome. */
export const RULES_PLAY_NOW_FOOTER_CLASS =
  'flex-shrink-0 px-4 pt-4 pb-5 bg-[#121212] border-t border-white/10 shadow-[0_-8px_24px_rgba(0,0,0,0.45)]';

/** Format fixture date/time for header (e.g. "Sun, Mar 2 7:00pm ET") */
function formatFixtureDateTime(startTimeUtc: string | undefined): string {
  if (!startTimeUtc) return 'TBD';
  try {
    const d = new Date(startTimeUtc);
    const formatted = format(d, 'EEE, MMM d h:mma');
    return `${formatted} ET`;
  } catch {
    return 'TBD';
  }
}

export interface RulesViewProps {
  /** Optional contest id override (used when rendering inside tab layout) */
  contestId?: string;
  /** When true, render without the standalone header (used inside MainLayout tabs) */
  embedded?: boolean;
  /** Popup variant: slide-up modal with fixture header (teams, date/time, X) */
  /** Entry variant: app entry page, no X button, no nav bar */
  variant?: 'embedded' | 'standalone' | 'popup' | 'entry';
  /** Close handler for popup variant */
  onClose?: () => void;
  /** Optional handler for Play Now (defaults to navigating to `/play?contestId=...`) */
  onPlayNow?: (contestId: string) => void;
}

/**
 * Rules View
 * Start page before entering a contest
 * Shows contest details, how to play, and Play Now button
 */
export default function RulesView({
  contestId: contestIdProp,
  embedded = false,
  variant: variantProp,
  onClose,
  onPlayNow,
}: RulesViewProps = {}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { contestId: contestIdParam } = useParams<{ contestId: string }>();
  const contestId = contestIdProp || contestIdParam;
  const variant = variantProp ?? (embedded ? 'embedded' : 'standalone');
  const isPopup = variant === 'popup';
  const isEmbedded = variant === 'embedded';
  const isEntry = variant === 'entry';

  // Fetch contest data from API
  const { data: contest, isLoading, error } = useGetSquareQuery(contestId || null);
  const { isAdsEnabled, adPlacements, isPlacementEnabled, gamConfig } = useTenant();

  // GAM placement for Rules view - path /{networkId}/{s1}/{s2}/rules, sends pid+s5=contestId
  // for targeting. Memoised: AdSlot keys its define/destroy effect on the sizes/targeting
  // identities, so rebuilding this inline re-requested the slot on every render.
  const rulesPlacement = useMemo(
    () =>
      isAdsEnabled && gamConfig?.networkId
        ? buildRulesPlacement(gamConfig.networkId, contestId)
        : null,
    [isAdsEnabled, gamConfig?.networkId, contestId]
  );

  const isLogoAdEnabled =
    isAdsEnabled && adPlacements?.SQUARES_OVER_LOGO && isPlacementEnabled('SQUARES_OVER_LOGO');
  const overLogoPlacement = useOverLogoPlacement(adPlacements?.SQUARES_OVER_LOGO, contestId);

  const handlePlayNow = () => {
    // Navigate to play view with contest ID
    if (contestId) {
      if (onPlayNow) {
        onPlayNow(contestId);
      } else {
        navigate(`/play?contestId=${contestId}`);
      }
    }
  };

  const handleClose = () => {
    if (isPopup && onClose) {
      onClose();
    } else if (isEntry) {
      navigate('/results');
    } else {
      navigate('/contests');
    }
  };

  const handleHelpClick = () => {
    const params = new URLSearchParams();
    params.set('contestId', contestId || '');
    params.set('returnTo', 'rules');
    params.set('rulesVariant', variant);

    const mode = new URLSearchParams(location.search).get('mode');
    if (mode) {
      params.set('mode', mode);
    }

    navigate(`/help?${params.toString()}`);
  };

  // Loading state
  if (isLoading) {
    return (
      <div className={`${isEmbedded || isEntry ? '' : 'min-h-screen'} bg-[#121212] flex flex-col`}>
        {!isEmbedded && !isEntry && <Header onClose={handleClose} />}
        <div className="flex-1 flex items-center justify-center">
          <div className="flex flex-col items-center gap-4">
            <Loader2 className="h-8 w-8 animate-spin text-[#53d337]" />
            <p className="text-zinc-400">Loading contest...</p>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !contest) {
    return (
      <div className={`${isEmbedded || isEntry ? '' : 'min-h-screen'} bg-[#121212] flex flex-col`}>
        {!isEmbedded && !isEntry && <Header onClose={handleClose} />}
        <div className="flex-1 flex items-center justify-center px-4">
          <div className="bg-red-900/20 border border-red-500/50 rounded-lg p-4 max-w-md">
            <p className="text-red-400 font-semibold">Error loading contest</p>
            <p className="text-red-300 text-sm mt-1">
              {error instanceof Error ? error.message : 'Contest not found'}
            </p>
            <button
              onClick={handleClose}
              className="mt-4 text-red-400 hover:text-red-300 underline"
            >
              Go back to contests
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Get contest data
  const prizePool = formatPrizePool(contest.total_prize_amount);
  const entries = contest.total_entries_to_date || 0;
  // Use feed abbreviations (UF, SCAR) when available, else derive from display name
  const team1 =
    contest.row_team_abbreviation?.trim() || getTeamCode(contest.row_team_display_name || '');
  const team2 =
    contest.col_team_abbreviation?.trim() || getTeamCode(contest.col_team_display_name || '');
  const fixtureDateTime = formatFixtureDateTime(contest.start_time_utc);

  // Get background image - prioritize custom rules image, then ad units, then default
  // Check for valid URL (not empty string, null, or undefined)
  const hasCustomRulesImage = contest.rules_image_url && contest.rules_image_url.trim().length > 0;
  const backgroundImageUrl = hasCustomRulesImage
    ? contest.rules_image_url
    : contest.ad_units && contest.ad_units.length > 0 && contest.gam_enabled
      ? contest.ad_units[0]?.imageUrl
      : undefined;
  const bannerAdClickUrl =
    contest.ad_units && contest.ad_units.length > 0 ? contest.ad_units[0]?.clickUrl : undefined;

  return (
    <div
      className={`${
        isEmbedded || isPopup
          ? `h-full min-h-0 overflow-hidden ${APP_COLUMN_BG}`
          : /* Standalone owns the whole page, so it owns the gutters too. Embedded
             * and popup variants sit *inside* a column that is already painted, so
             * they must keep the column colour or they'd paint gutter over content. */
            `h-[100dvh] max-h-[100dvh] overflow-hidden ${APP_GUTTER_BG}`
      } flex flex-col`}
    >
      {/* Mobile-first: locked to viewport so footer stays in fold; max-w on md+ */}
      <div
        className={`${
          isEmbedded || isPopup
            ? 'flex-1 min-h-0 w-full flex flex-col'
            : `w-full h-full min-h-0 ${APP_COLUMN_BG} ${DESKTOP_COLUMN} ${DESKTOP_COLUMN_FRAME} flex flex-col`
        } min-h-0`}
      >
        {/* Popup: fixture header (teams, date/time, Help, X) */}
        {isPopup && (
          <div className="flex-shrink-0 flex items-center justify-between px-4 py-3 border-b border-[#373737] bg-[#121212]">
            <div className="flex flex-col">
              <p
                className="text-white font-bold"
                style={{
                  fontFamily: '"Saira Extra Condensed", sans-serif',
                  fontSize: '20px',
                  lineHeight: '24px',
                }}
              >
                {team1} vs {team2}
              </p>
              <p
                className="text-zinc-500 text-sm mt-0.5"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {fixtureDateTime}
              </p>
            </div>
            <button
              onClick={handleClose}
              className="p-2 -mr-2 text-white hover:bg-white/10 rounded-full transition-colors"
              aria-label="Close"
            >
              <X className="h-6 w-6" strokeWidth={2} />
            </button>
          </div>
        )}

        {/* Entry: fixture header (teams, date/time) - no X button */}
        {isEntry && (
          <div className="flex-shrink-0 flex items-center px-4 py-3 border-b border-[#373737] bg-[#121212]">
            <div className="flex flex-col">
              <p
                className="text-white font-bold"
                style={{
                  fontFamily: '"Saira Extra Condensed", sans-serif',
                  fontSize: '20px',
                  lineHeight: '24px',
                }}
              >
                {team1} vs {team2}
              </p>
              <p
                className="text-zinc-500 text-sm mt-0.5"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {fixtureDateTime}
              </p>
            </div>
          </div>
        )}

        {/* Header with close button (standalone rules page only) */}
        {variant === 'standalone' && (
          <div className="flex-shrink-0">
            <Header onClose={handleClose} />
          </div>
        )}

        {/* Scrollable content + footer pinned in viewport: only this section scrolls */}
        <div className="flex flex-col flex-1 min-h-0 overflow-hidden">
          <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden overscroll-contain">
            {/* Banner Ad Background */}
            <div
              className={`relative flex-shrink-0 overflow-hidden ${READABLE_CONTENT}`}
              style={{
                aspectRatio: '375 / 351',
                backgroundImage: backgroundImageUrl
                  ? `url(${backgroundImageUrl})`
                  : `url(/man-image.svg)`,
                backgroundSize: 'cover',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'center',
                backgroundColor: '#121212',
              }}
            >
              {/* Gradient overlay for readability */}
              <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-black/50" />

              {/* Banner Ad Component (for clickable ads) - only show if using ad units, not custom rules image */}
              {!hasCustomRulesImage &&
                backgroundImageUrl &&
                contest.ad_units &&
                contest.ad_units.length > 0 && (
                  <BannerAdComponent
                    imageUrl={backgroundImageUrl}
                    clickUrl={bannerAdClickUrl}
                    altText="Contest Banner Advertisement"
                    slotId={`contest-${contest.squares_contest_id}-banner`}
                  />
                )}

              {/* Overlay Content */}
              <div className="relative z-10 h-full flex flex-col items-center justify-start px-4 pt-6">
                {/* Sponsor Logo Section - Reduced size to fit all text */}
                <div className="mb-3">
                  <div className="relative h-32 w-32 flex items-center justify-center">
                    {/* Over Logo Ad Slot (1x1 tracking pixel, positioned just above logo) */}
                    {isLogoAdEnabled && overLogoPlacement && (
                      <div className="absolute bottom-full w-[1px] h-[1px] overflow-hidden pointer-events-none">
                        <AdSlot
                          divId={generateAdDivId(adPlacements, 'SQUARES_OVER_LOGO', true, 3)}
                          adPlacement={overLogoPlacement}
                          placement="squares-over-logo"
                        />
                      </div>
                    )}
                    {contest.badge_image_url && contest.badge_image_url.trim().length > 0 ? (
                      <img
                        src={contest.badge_image_url}
                        alt="Squares Logo"
                        className="h-full w-full object-contain"
                      />
                    ) : (
                      <img
                        src="/DFS_NFL_Wrangler_Squares_Logo_1080x1080%201.svg"
                        alt="Squares Logo"
                        className="h-full w-full object-contain"
                      />
                    )}
                  </div>
                </div>

                {/* Prize Pool Section */}
                <div className="text-center mb-2">
                  <p
                    className="text-white mb-1.5"
                    style={{
                      fontFamily: '"Saira Extra Condensed", sans-serif',
                      fontWeight: 800,
                      fontSize: '24px',
                      lineHeight: '28px',
                      letterSpacing: '0.5%',
                      textAlign: 'center',
                    }}
                  >
                    WIN YOUR SHARE OF
                  </p>
                  <p
                    className="text-[#53d337] mb-2"
                    style={{
                      fontFamily: '"Saira Extra Condensed", sans-serif',
                      fontWeight: 800,
                      fontSize: '32px',
                      lineHeight: '36px',
                      letterSpacing: '0.5px',
                    }}
                  >
                    {prizePool}
                  </p>
                </div>

                {/* Entries Display */}
                <div className="rounded-lg px-4 py-2 bg-black/60 backdrop-blur-sm border border-white/20 flex items-center gap-2">
                  <User className="w-4 h-4" />
                  <span
                    style={{
                      fontFamily: 'Inter, sans-serif',
                      fontWeight: 400,
                      fontSize: '14px',
                      color: '#ffffff',
                    }}
                  >
                    {entries.toLocaleString()} Entries
                  </span>
                </div>
              </div>
            </div>

            {/* How To Play Section */}
            <div className={`flex-1 px-4 py-6 ${READABLE_CONTENT}`}>
              <div className="flex items-center justify-between mb-6">
                <h2
                  className="text-white"
                  style={{
                    fontFamily: '"Saira Extra Condensed", sans-serif',
                    fontWeight: 800,
                    fontSize: '24px',
                    lineHeight: '28px',
                    letterSpacing: '0.5px',
                  }}
                >
                  HOW TO PLAY
                </h2>
                <button
                  onClick={handleHelpClick}
                  className="p-2 -mr-2 text-white hover:bg-white/10 rounded-full transition-colors"
                  aria-label="Help - How to play"
                >
                  <HelpCircle className="h-6 w-6" strokeWidth={2} />
                </button>
              </div>

              {/* Steps */}
              <div className="space-y-6">
                {/* Step 1 */}
                <div className="flex gap-4">
                  <div className="flex flex-col items-center flex-shrink-0">
                    <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center">
                      <span
                        className="text-black"
                        style={{
                          fontFamily: '"Saira Extra Condensed", sans-serif',
                          fontWeight: 800,
                          fontSize: '18px',
                          lineHeight: '20px',
                        }}
                      >
                        1
                      </span>
                    </div>
                    <div className="w-px h-12 bg-[#373737] mt-2" />
                  </div>
                  <div className="flex-1 pt-1">
                    <h3
                      className="text-white mb-1"
                      style={{
                        fontFamily: '"Saira Extra Condensed", sans-serif',
                        fontWeight: 800,
                        fontSize: '18px',
                        lineHeight: '22px',
                        letterSpacing: '0.5px',
                      }}
                    >
                      PICK YOUR SQUARE
                    </h3>
                    <p
                      className="text-zinc-400"
                      style={{
                        fontFamily: 'Inter, sans-serif',
                        fontWeight: 400,
                        fontSize: '14px',
                        lineHeight: '20px',
                      }}
                    >
                      Choose a square on the 10×10 grid before the specified game starts.
                    </p>
                  </div>
                </div>

                {/* Step 2 */}
                <div className="flex gap-4">
                  <div className="flex flex-col items-center flex-shrink-0">
                    <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center">
                      <span
                        className="text-black"
                        style={{
                          fontFamily: '"Saira Extra Condensed", sans-serif',
                          fontWeight: 800,
                          fontSize: '18px',
                          lineHeight: '20px',
                        }}
                      >
                        2
                      </span>
                    </div>
                    <div className="w-px h-12 bg-[#373737] mt-2" />
                  </div>
                  <div className="flex-1 pt-1">
                    <h3
                      className="text-white mb-1"
                      style={{
                        fontFamily: '"Saira Extra Condensed", sans-serif',
                        fontWeight: 800,
                        fontSize: '18px',
                        lineHeight: '22px',
                        letterSpacing: '0.5px',
                      }}
                    >
                      NUMBERS ARE RANDOMLY ASSIGNED
                    </h3>
                    <p
                      className="text-zinc-400"
                      style={{
                        fontFamily: 'Inter, sans-serif',
                        fontWeight: 400,
                        fontSize: '14px',
                        lineHeight: '20px',
                      }}
                    >
                      When the game begins, numbers 0–9 are randomly assigned to the rows (Team A)
                      and columns (Team B). Your square&apos;s numbers are based on where it sits on
                      the grid.
                    </p>
                  </div>
                </div>

                {/* Step 3 */}
                <div className="flex gap-4">
                  <div className="flex flex-col items-center flex-shrink-0">
                    <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center">
                      <span
                        className="text-black"
                        style={{
                          fontFamily: '"Saira Extra Condensed", sans-serif',
                          fontWeight: 800,
                          fontSize: '18px',
                          lineHeight: '20px',
                        }}
                      >
                        3
                      </span>
                    </div>
                    <div className="w-px h-12 bg-[#373737] mt-2" />
                  </div>
                  <div className="flex-1 pt-1">
                    <h3
                      className="text-white mb-1"
                      style={{
                        fontFamily: '"Saira Extra Condensed", sans-serif',
                        fontWeight: 800,
                        fontSize: '18px',
                        lineHeight: '22px',
                        letterSpacing: '0.5px',
                      }}
                    >
                      MATCH THE SCORE TO WIN
                    </h3>
                    <p
                      className="text-zinc-400"
                      style={{
                        fontFamily: 'Inter, sans-serif',
                        fontWeight: 400,
                        fontSize: '14px',
                        lineHeight: '20px',
                      }}
                    >
                      At the end of each scoring period (half, quarter, etc.), look at the last
                      digit of each team&apos;s score. Team A&apos;s last digit = row number, Team
                      B&apos;s last digit = column number. If your square matches both numbers, you
                      win!
                    </p>
                    <p
                      className="text-zinc-400 mt-2"
                      style={{
                        fontFamily: 'Inter, sans-serif',
                        fontWeight: 400,
                        fontSize: '14px',
                        lineHeight: '20px',
                      }}
                    >
                      Example: Halftime score — Team A: 42 (last digit 2), Team B: 33 (last digit
                      3). The winning square is Row 2 / Column 3. If you selected that square, you
                      win! If multiple people selected it, the prize is split evenly among those
                      users.
                    </p>
                  </div>
                </div>

                {/* No closing paragraph: the previous one promised daily NBA Playoff games
                    (DKPLAT-721). Squares now runs across sports, so a sport- and
                    schedule-specific promise cannot be stated generically and was dropped
                    rather than reworded. */}
              </div>
            </div>

            {/* GAM Ad - Rules placement (path {basePath}/rules) */}
            {rulesPlacement && (
              <div className="px-4 py-4">
                <AdSlot
                  divId="squares-rules-ad"
                  adPlacement={rulesPlacement}
                  placement="squares-rules"
                  /*
                   * `squares-rules-advert` is a styling hook, not decoration — `index.css`
                   * reaches the GPT iframe through it to force a taller box on iOS. Kept as
                   * a real class rather than reusing a `data-test-id`, so the selector is
                   * obviously load-bearing and does not look like a test artefact someone
                   * can tidy away.
                   */
                  className="squares-rules-advert rounded-lg [&_iframe]:rounded-lg max-w-[320px] mx-auto"
                />
              </div>
            )}
          </div>

          {/* Play Now — flex-pinned footer; panel shell sits above DK tab bar */}
          <div className={RULES_PLAY_NOW_FOOTER_CLASS}>
            <button
              onClick={handlePlayNow}
              className="w-full rounded-lg flex items-center justify-center gap-2 transition-opacity hover:opacity-90 active:opacity-80"
              style={{
                backgroundColor: '#53D337',
                fontFamily: '"Saira Extra Condensed", sans-serif',
                fontWeight: 800,
                fontSize: '18px',
                lineHeight: '22px',
                letterSpacing: '0.5px',
                padding: '14px 24px',
              }}
            >
              <span style={{ color: '#000000' }}>PLAY NOW</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
